import React, { useCallback, useEffect, useMemo, useRef } from "react";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  SWITCH_NUMBER_STRING,
  SWITCH_NUMBER_OP,
} from "lookup/GlobalConstants";
import {
  SWIM_STATES_FOR_SELECT,
  DEFAULT_SWIM_SWITCH_SORT,
} from "lookup/SwimConstants";
import { showNotification } from "services/NotificationService";
import SwimSwitchServerSideReactTable from "commons/table/SwimSwitchServerSideReactTable";
import { createColumnListForSwimSwitchList } from "./CreateColumnListForSwimSwitchList";

const Tip = (props) => (
  <div style={{ textAlign: "center", color: "#888", fontSize: "0.9em" }}>
    <em>{props.children}</em>
  </div>
);

const DEFAULT_PAGE_SIZE = 15;

const SwimSwitchListCommon = (props) => {
  const {
    columnList,
    isSortFilterEnabledForCust,
    isShowBioSimilarToggle,
    updateInterventionList,
    listState,
    isError,
    resetError,
    toggleSelection,
    isSelectAll,
    selected,
    resetTableSelection,
    shouldFetchOnEmptyFilters,
    data,
    pages,
    loading,
    totalElements,
  } = props;

  // Track last dispatched request to avoid duplicate backend calls
  const lastRequestKeyRef = useRef(null);

  const computedColumnList = useMemo(
    () => createColumnListForSwimSwitchList(columnList, isSortFilterEnabledForCust),
    [columnList, isSortFilterEnabledForCust]
  );

  const updateData = useCallback(
    (listOptionsFromTable) => {
      let newFiltered = [...listOptionsFromTable.filtered];
      let newSorted = [...listOptionsFromTable.sorted];

      newFiltered = newFiltered.filter((element) => {
        if (typeof element.value === "string" || Array.isArray(element.value)) {
          return element.value.length > 0;
        }
        if (typeof element.value === "boolean") {
          return true;
        }
        return element.value !== undefined && element.value !== null;
      });

      if (newSorted !== DEFAULT_SWIM_SWITCH_SORT) {
        newSorted = newSorted.map((element) => ({
          ...element,
          isDefault: false,
        }));
      }

      const switchNumberIndex = newFiltered.findIndex(
        (element) => element.id === "switchNumber"
      );
      if (switchNumberIndex !== -1) {
        newFiltered[switchNumberIndex] = {
          ...newFiltered[switchNumberIndex],
          operator: SWITCH_NUMBER_OP,
          id: SWITCH_NUMBER_STRING,
        };
      }

      const typeIndex = newFiltered.findIndex(
        (element) => element.id === "type"
      );
      if (typeIndex !== -1) {
        newFiltered[typeIndex] = {
          ...newFiltered[typeIndex],
          value: newFiltered[typeIndex].value.map((value) =>
            value.name ? value.name : value
          ),
        };
      }

      if (isShowBioSimilarToggle) {
        const clientNameFilterExists = newFiltered.some(
          (f) => f.id === "clientNameAbbreviation"
        );
        const customerNameFilterExists = newFiltered.some(
          (f) => f.id === "customerNameAbbreviation"
        );

        if (!clientNameFilterExists) {
          newFiltered.push({
            id: "clientNameAbbreviation",
            value: "WMT",
          });
        }

        if (!customerNameFilterExists) {
          newFiltered.push({
            id: "customerNameAbbreviation",
            value: "WMT",
          });
        }
      }

      // Avoid duplicate backend calls for identical request params
      const requestKey = JSON.stringify({
        page: listOptionsFromTable.page,
        pageSize: listOptionsFromTable.pageSize,
        sorted: newSorted,
        filtered: newFiltered,
      });
      if (lastRequestKeyRef.current === requestKey) {
        return;
      }
      lastRequestKeyRef.current = requestKey;

      updateInterventionList(
        listOptionsFromTable.pageSize,
        listOptionsFromTable.page,
        newSorted,
        newFiltered,
        listOptionsFromTable.resized,
        listOptionsFromTable.expanded
      );
    },
    [isShowBioSimilarToggle, updateInterventionList]
  );

  const onUpdateTable = useCallback(
    (filterValues, sortBy, pageSize, pageNo) => {
    const newListState = {
      page: pageNo,
      pageSize: pageSize,
      sorted: sortBy || [],
      filtered: filterValues,
        resized: listState.resized,
        expanded: listState.expanded,
    };
      updateData(newListState);
    },
    [listState, updateData]
  );

  useEffect(() => {
    if (isError) {
      showNotification({
        title: "Error Loading Data!",
        message: "You have entered invalid information.",
        position: "bl",
        type: "error",
      });
      resetError();
    }
  }, [isError, resetError]);

  const currentFiltersForTable = listState.filtered.map((f) => {
        if (f.id === SWITCH_NUMBER_STRING) {
            return { ...f, id: "switchNumber" };
        }
        return f;
    });

    return (
      <Row>
        <Col md={12}>
          <SwimSwitchServerSideReactTable
            columnDefination={computedColumnList}
            data={data}
            currentPage={listState.page}
            sorted={listState.sorted}
            filters={currentFiltersForTable}
            totalPages={pages}
            totalRecords={totalElements === "loading" ? 0 : totalElements}
            loading={loading}
            overrideRowId={"wfItemId"}
            pageSizeOptions={[5, 10, 15, 20, 30, 50]}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            onUpdateTableData={onUpdateTable}
            enableMultiRowSelection={!!toggleSelection}
            addRowSelection={!!toggleSelection}
            onRowSelect={toggleSelection}
            toggleAllRowsSelect={isSelectAll}
            globalSelected={selected ? selected : []}
            resetTableSelection={resetTableSelection || null}
            shouldFetchOnEmptyFilters={shouldFetchOnEmptyFilters}
          />
          <Tip>
            Tip: Hold shift when sorting to multi-sort and disable sort!
          </Tip>
        </Col>
      </Row>
    );
};

export default SwimSwitchListCommon;
